import { useAppDispatch } from '@/hooks';
import dayjs from 'dayjs';
import React, { Key, useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { FetchColumnsType } from '@/components/common/table-base';
import { TextField } from '@/components/text-field/TextField';
import EmptyDataWithIcon from '@components/EmptyData/EmptyDataWithIcon';
import AppTooltip from '@components/app-tooltip/AppTooltip';
import { BaseButton } from '@components/base-button/BaseButton';
import FormBaseFilter, { RefActionControl } from '@components/base-filter';
import BaseAction from '@components/base-filter/BaseAction';
import TableFetch from '@components/common/table-fetch';

import { setAlertNotification } from '@redux/globalReducer';

import useAuthorization, * as s from '@hooks/useAuthorization';
import useFetch from '@hooks/useFetch';
import { useFilterConfigs } from '@hooks/useFilterConfigs';
import useMutation from '@hooks/useMutation';

import { updatePhrasesIndex } from '@/utils/services/template';
import { ROUTER_IDS } from '@utils/constants';
import { FORMAT_DATE_EN_HH_MM_SS, TYPE } from '@utils/constants/AppConstants';
import { DataViewer } from '@utils/helpers/common';
import { formatDateTime, getEndOfDay, isNonNegativeInteger } from '@utils/helpers/globalHelper';

import IconAddNew from '@assets/icons/base-filter/icon-add-new.svg';

import PhrasePopup, { IPhaseCreateEdit } from '../../phrase-create-edit/components/CreateEditPopup';
import { IQueryFilterParams, generateFilter } from '../ultis';
import PrimaryFormFilter, { IPhraseCategory } from './PrimaryFormFilter';

export interface IPhrase {
  id: string;
  categoryId: string;
  categoryName: string;
  content: string;
  createdBy: string;
  createdDate: string;
  createdName: string;
  title: string;
  titleSearch: string;
  updatedBy: string;
  updatedDate: string;
  updatedName: string;
  updatedNameSearch: string;
  phraseIndex?: number;
}

export default function PhraseList() {
  const { t } = useTranslation();
  const mounted = useRef<any>(false);
  const refList = useRef<any>(null);
  const tableRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const filterTableRef = useRef<RefActionControl>({
    toggle: () => {}
  });
  const [showActions, setShowActions] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const { data: defaultFilterConfigs, update: updateFilterConfigs } = useFilterConfigs<FieldValues>(ROUTER_IDS.PHRASE_MASTER_LIST);

  const { isInternalUser, hasPermission } = useAuthorization();
  const { data: phraseCategories, loading: loadingPhraseCategory } = useFetch<IPhraseCategory[]>(`/prj/phrases/category`, 'GET');
  const { mutate: deletePhrases } = useMutation(`/prj/phrases`, {
    method: 'DELETE',
    showToastError: true
  });

  const generateDefaultFilterForm = () => {
    const results = {
      form: {
        primary: {}
      },
      query: {}
    };

    if (!defaultFilterConfigs?.primary) return results;

    const { updatedDateFrom, updatedDateTo } = defaultFilterConfigs.primary;

    const rawCategoryIds = defaultFilterConfigs.primary.categoryId;

    // Normalize categoryId về array
    const categoryIds: any[] = Array.isArray(rawCategoryIds)
      ? rawCategoryIds
      : rawCategoryIds === null || rawCategoryIds === undefined || rawCategoryIds === ''
        ? []
        : typeof rawCategoryIds === 'string'
          ? rawCategoryIds
              .split(/[;,]/)
              .map((id) => id.trim())
              .filter(Boolean)
          : [rawCategoryIds];

    const normalizedCategoryIds = categoryIds
      .map((item: any) => {
        if (item && typeof item === 'object') {
          return item.value ?? item.id ?? item.key;
        }

        return item;
      })
      .filter((id: any) => id !== null && id !== undefined && id !== '');

    const mappedCategoryIds = normalizedCategoryIds.map((id: any) => {
      const cat = phraseCategories?.find((c: any) => c.id === id);

      return cat
        ? {
            label: cat.name,
            value: cat.id
          }
        : {
            label: id,
            value: id
          };
    });

    results.form = {
      primary: {
        ...defaultFilterConfigs.primary,
        categoryId: mappedCategoryIds.length ? mappedCategoryIds : undefined,
        updatedDateFrom: updatedDateFrom ? dayjs(updatedDateFrom) : undefined,
        updatedDateTo: updatedDateTo ? dayjs(updatedDateTo) : undefined
      }
    };

    const categoryIdForQuery = normalizedCategoryIds.join('; ');

    results.query = {
      filter: generateFilter({
        keyword: '',
        categoryId: categoryIdForQuery || undefined,
        updatedDateFrom: defaultFilterConfigs.primary.updatedDateFrom ?? undefined,
        updatedDateTo: defaultFilterConfigs.primary.updatedDateTo ?? undefined
      })
    };

    return results;
  };

  const formFilter = useForm({ defaultValues: generateDefaultFilterForm().form });
  const [filter, setFilter] = useState(generateDefaultFilterForm().query);

  useEffect(() => {
    if (!defaultFilterConfigs?.primary) return;
    if (!phraseCategories?.length) return;

    const defaultValues = generateDefaultFilterForm();

    formFilter.reset(defaultValues.form);
    setFilter(defaultValues.query);
  }, [defaultFilterConfigs, phraseCategories]);

  // Popup create/edit
  const [editData, setEditData] = useState<IPhaseCreateEdit>({});
  const [openEditPopup, setOpenEditPopup] = useState<boolean>(false);

  const handleUpdateNo = async (data: any, e: any, oldValue: number | undefined) => {
    const phraseIndex = e.target?.value;
    if (!phraseIndex && !isNonNegativeInteger(oldValue)) return;
    try {
      await updatePhrasesIndex(data.id as string, { pharseIndex: isNonNegativeInteger(phraseIndex) ? Number(phraseIndex) : null });
      tableRef.current?.refreshData();
    } catch {}
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const columns: FetchColumnsType<IPhrase> = [
    {
      title: 'NO',
      key: 'no',
      width: !isInternalUser ? 70 : 50,
      className: 'max-w-[50px] h-[38px]',
      render: (_value: any, record: IPhrase, index: number) => {
        return (
          <div className='whitespace-nowrap align-top [&_.ant-input]:!px-2 [&_.ant-input]:!py-0 [&_.ant-input]:!h-[22px]' key={index}>
            {!isInternalUser ? (
              <TextField
                className='text-center !rounded-[6px]'
                defaultValue={record.phraseIndex?.toString()}
                placeholder='-'
                onBlur={(e) => handleUpdateNo(record, e, record?.phraseIndex)}
                onPressEnter={(e) => handleUpdateNo(record, e, record?.phraseIndex)}
                onKeyDown={(e) => {
                  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                  if (allowedKeys.includes(e.key)) return;
                  if (e.ctrlKey || e.metaKey) return;
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            ) : (
              record.phraseIndex
            )}
          </div>
        );
      }
    },
    {
      title: t('master_data:phrase:name'),
      key: 'name',
      width: 700,
      className: 'max-w-[700px] h-[38px]',
      render: (_value: any, record: IPhrase) => {
        return (
          <AppTooltip title={record.title}>
            {hasPermission(s.PHRASE_LIST_BUTTON_EDIT, s.V) ? (
              <Link
                className='link-custom'
                onClick={(event) => {
                  event.preventDefault();
                  setEditData({ ...record });
                  setOpenEditPopup(true);
                }}
                to={''}
              >
                {record.title}
              </Link>
            ) : (
              <span>{record.title}</span>
            )}
          </AppTooltip>
        );
      },
      ellipsis: true
    },
    {
      title: t('master_data:phrase:category'),
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 400,
      className: 'max-w-[400px] h-[38px]',
      render: (_value, record: IPhrase) => {
        return DataViewer.display(record.categoryName);
      },
      tooltip(_value, record: IPhrase) {
        return DataViewer.display(record.categoryName);
      },
      ellipsis: true
    },
    {
      title: t('master_data:phrase:editor'),
      dataIndex: 'updatedName',
      width: 150,
      className: 'max-w-[150px] h-[38px]',
      key: 'updater',
      render: (_: any, record: IPhrase) => DataViewer.display(record?.updatedName ?? record?.createdName),
      tooltip: (_: any, record: IPhrase) => DataViewer.display(record?.updatedName ?? record?.createdName),
      ellipsis: true
    },
    {
      title: t('master_data:updated_date'),
      dataIndex: 'updatedDate',
      key: 'updatedDate',
      width: 170,
      className: 'max-w-[170px]',
      render: (_: any, record: IPhrase) => DataViewer.localTimeBy(record?.updatedDate || record?.createdDate, FORMAT_DATE_EN_HH_MM_SS),
      tooltip: (_: any, record: IPhrase) => DataViewer.localTimeBy(record?.updatedDate || record?.createdDate, FORMAT_DATE_EN_HH_MM_SS)
    }
  ];

  const generateFilterHeader = () => {
    return (
      <div className='mb-[12px]'>
        <FormProvider {...formFilter}>
          <FormBaseFilter
            changeAction
            primaryActionFieldName='primary'
            onSubmit={(values) => {
              updateFilter(values);
            }}
            loading={loadingPhraseCategory}
            searchBox={{
              placeholder: DataViewer.display(
                `${t('placeholder:search')}: ${[t('master_data:phrase:name'), t('master_data:phrase:editor')].join(t('placeholder:seperate') ?? ',')}`
              )
            }}
            tagSection={{
              renderTag: (fieldKey, fieldValue) => {
                if (fieldKey === 'categoryId') {
                  const category = phraseCategories?.find((i) => i.id === fieldValue || i.id === fieldValue?.value || i.id === fieldValue?.key);

                  return category?.name || fieldValue?.label || fieldValue;
                }
                if (['updatedDateFrom', 'updatedDateTo'].includes(fieldKey)) {
                  return <>{formatDateTime(fieldValue)}</>;
                }

                return <>{fieldValue}</>;
              }
            }}
            primaryAction={
              !showActions ? (
                <div className='flex items-center gap-2'>
                  <BaseAction
                    label='button:filter'
                    popoverContent={
                      <PrimaryFormFilter
                        onSubmit={(values) => {
                          formFilter.reset((previous) => ({ ...previous, primary: { ...values } }));
                          formFilter.handleSubmit((values) => updateFilter(values))();
                        }}
                        defaultValues={formFilter.getValues('primary') ?? {}}
                      />
                    }
                    refControl={filterTableRef}
                  />
                  {isInternalUser ? null : (
                    <BaseButton
                      size='medium'
                      type='tertiary'
                      onClick={() => {
                        setShowActions(true);
                      }}
                    >
                      <span>{t('sider:setting_options')}</span>
                    </BaseButton>
                  )}
                </div>
              ) : (
                <div className='flex items-center gap-2'>
                  {selectedKeys.length > 0 && <div className='font-[500] mr-2'>{`${selectedKeys.length} ${t('common:selected')}`}</div>}
                  <BaseButton
                    size='medium'
                    type='tertiary'
                    onClick={() => {
                      setSelectedKeys([]);
                      setShowActions(false);
                      tableRef.current?.clearSelection?.();
                    }}
                  >
                    <span>{t('button:cancel')}</span>
                  </BaseButton>

                  <BaseButton
                    size='medium'
                    type='outline-red'
                    className='!min-w-10'
                    disabled={!selectedKeys.length}
                    onClick={async () => {
                      if (!hasPermission(s.DELETE_PHRASE_LIST, s.C)) return;
                      if (!selectedKeys.length) return;

                      const result = await tableRef.current?.deleteItems?.(selectedKeys, {
                        type: 'discard',
                        title: t('button:delete'),
                        msg: t('sider:confirm_popup')
                      });

                      if (result !== undefined) {
                        setShowActions(false);
                        setSelectedKeys([]);
                      }
                    }}
                  >
                    <span>{t('button:delete')}</span>
                  </BaseButton>
                </div>
              )
            }
            secondaryAction={null}
            thirdAction={null}
          />
        </FormProvider>
      </div>
    );
  };

  const updateFilter = (values: FieldValues) => {
    const updatedDateFrom = values?.primary?.updatedDateFrom ? formatDateTime(values?.primary?.updatedDateFrom) : undefined;
    const updatedDateTo = values?.primary?.updatedDateTo ? getEndOfDay(values?.primary?.updatedDateTo) : undefined;
    const categoryIds = (values?.primary?.categoryId ?? [])
      .map((e: any) => (e && typeof e === 'object' ? e.value : e))
      .filter((v: any) => v !== null && v !== undefined && v !== '');
    const formValues: IQueryFilterParams = {
      keyword: values?.search,
      categoryId: categoryIds.join('; '),
      updatedDateFrom,
      updatedDateTo
    };
    setFilter({ filter: generateFilter(formValues) });
    updateFilterConfigs({
      ...values,
      primary: {
        ...values.primary,
        categoryId: categoryIds,
        updatedDateFrom,
        updatedDateTo
      }
    });
  };

  const handleDelete = async (keys: React.Key[], records: any[]) => {
    const result = await deletePhrases({
      ids: records
        .filter((i) => i)
        .map((i) => ({
          id: i.id,
          version: i.version
        }))
    });
    if (!result) return 0;

    const { data } = result;
    if (data.successCount === keys.length) {
      dispatch(
        setAlertNotification({
          show: true,
          type: TYPE.SUCCESS,
          message: t('common:MSG_C_003', { item: t('master_data:phrase:delete') })
        })
      );
    }

    if (data.successCount < keys.length) {
      dispatch(
        setAlertNotification({
          show: true,
          type: TYPE.SUCCESS,
          message: t('common:MSG_C_020', { deletable: data.successCount, total: keys.length })
        })
      );
    }
    return data.successCount;
  };

  const addNewPhrase = () => {
    setEditData({});
    setOpenEditPopup(true);
    setFilter({ ...filter });
    setSelectedKeys([]);
    setShowActions(false);
  };

  return (
    <div ref={refList}>
      <div className='grid grid-cols-2 gap-4 page-list-header mb-[12px]'>
        <div className='title-24'>{t('sider:phrase')}</div>
        <div className='text-end'>
          {hasPermission(s.PHRASE_LIST_BUTTON_CREATE, s.V) && (
            <BaseButton size='medium' icon={<IconAddNew style={{ verticalAlign: 'sub' }} />} onClick={addNewPhrase} className='float-right'>
              {t('template_layout:create_button')}
            </BaseButton>
          )}
        </div>
      </div>
      {(hasPermission(s.PHRASE_LIST_BUTTON_CREATE, s.V) || hasPermission(s.PHRASE_LIST_BUTTON_EDIT, s.V)) && (
        <PhrasePopup
          getContainer={() => refList.current ?? document.body}
          data={editData}
          openModal={openEditPopup}
          onSubmit={() => {
            setOpenEditPopup(false);
            setFilter({ ...filter });
          }}
          onCancel={() => {
            setOpenEditPopup(false);
          }}
        />
      )}
      <div className='data-table'>
        <TableFetch
          ref={tableRef}
          tableHeader={generateFilterHeader()}
          apiEndpoint='/prj/phrases/search'
          apiMethod='POST'
          columns={columns}
          filterDefault={filter}
          showActions={showActions}
          onSelectionChange={(keys, rows) => {
            setSelectedKeys(keys);
          }}
          scrollTable={{ maxHeight: '100vh - 240px', x: 1000 }}
          showDelete={hasPermission(s.DELETE_PHRASE_LIST, s.V)}
          showDownload={false}
          groupBy='categoryId'
          groupRowRender={(record) => {
            const label = record.children.length ? record.children[0]?.categoryName : '';
            return (
              <>
                {label}
                <span className=''>({record.children.length})</span>
              </>
            );
          }}
          handleDeleteClick={hasPermission(s.DELETE_PHRASE_LIST, s.C) ? handleDelete : undefined}
          deleteConfirmPopup={{
            type: 'discard',
            title: t('common:MSG_C_012:title', { field: t('master_data:phrase:label') }) ?? '',
            msg: t('common:MSG_C_012:description', { field: t('master_data:phrase:label') }) ?? ''
          }}
          emptyDataWithoutFilter={
            <EmptyDataWithIcon
              title={{
                content: 'master_data:phrase:create_new_label'
              }}
              description={{
                content: 'master_data:phrase:no_data_description'
              }}
              button={{
                label: 'master_data:phrase:create',
                props: {
                  onClick: addNewPhrase
                }
              }}
            />
          }
          rowSelection={!hasPermission(s.DELETE_PHRASE_LIST, s.C) ? null : {}}
        />
      </div>
    </div>
  );
}
